import type { DocumentMeta, ExportSettings } from '../core/types';
import type { Engine } from '../engine/engine';

const MIME_TYPES: Record<Exclude<ExportSettings['format'], 'pdf'>, string> = {
  png: 'image/png',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

export const EXPORT_EXTENSIONS: Record<ExportSettings['format'], string> = {
  png: 'png',
  jpeg: 'jpg',
  webp: 'webp',
  pdf: 'pdf',
};

/** Flattens the document into a 2D canvas at `scale`, ready to encode. */
export function renderToCanvas(
  engine: Engine,
  doc: DocumentMeta,
  scale = 1,
  flattenOnWhite = false,
): HTMLCanvasElement {
  const { width, height, data } = engine.flatten(doc);

  const full = document.createElement('canvas');
  full.width = width;
  full.height = height;
  const ctx = full.getContext('2d')!;
  ctx.putImageData(new ImageData(new Uint8ClampedArray(data), width, height), 0, 0);

  if (flattenOnWhite) {
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.globalCompositeOperation = 'source-over';
  }

  if (scale === 1) return full;

  const scaled = document.createElement('canvas');
  scaled.width = Math.max(1, Math.round(width * scale));
  scaled.height = Math.max(1, Math.round(height * scale));
  const scaledCtx = scaled.getContext('2d')!;
  scaledCtx.imageSmoothingEnabled = true;
  scaledCtx.imageSmoothingQuality = 'high';
  scaledCtx.drawImage(full, 0, 0, scaled.width, scaled.height);
  return scaled;
}

export async function exportDocument(
  engine: Engine,
  doc: DocumentMeta,
  settings: ExportSettings,
): Promise<Blob> {
  if (settings.format === 'pdf') {
    const { documentToPdf } = await import('./pdf');
    const buffer = await documentToPdf(engine, doc, settings);
    return new Blob([buffer], { type: 'application/pdf' });
  }
  // JPEG has no alpha channel, so transparency has to be resolved to white here
  // rather than being silently turned black by the encoder.
  const imageFormat = settings.format;
  const canvas = renderToCanvas(engine, doc, settings.scale, imageFormat === 'jpeg');
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, MIME_TYPES[imageFormat], settings.quality);
  });
  if (!blob) throw new Error('The browser failed to encode the image');
  return blob;
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  // Revoking immediately can cancel the download in Firefox.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function estimatedPixelCount(doc: DocumentMeta, scale: number): string {
  const width = Math.max(1, Math.round(doc.width * scale));
  const height = Math.max(1, Math.round(doc.height * scale));
  return `${width} × ${height}`;
}
